import React, { Fragment, useEffect, useMemo, useState } from "react"

import { useTranslation } from "react-i18next"
import { useQuery } from "react-query"
import { useParams, useSearchParams } from "react-router-dom"
import { useStoreApi } from "reactflow"

import CodeIcon from "@mui/icons-material/Code"
import LaunchOutlinedIcon from "@mui/icons-material/LaunchOutlined"
import PolylineIcon from "@mui/icons-material/Polyline"
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined"
import { Grid, Skeleton } from "@mui/material"
import { Button } from "@synapse-analytics/synapse-ui"
import type { AxiosError, AxiosResponse } from "axios"

import { ValueBlock } from "../../../../components/data-display/ValueBlock"
import { getTheme } from "../../../../hooks/UseTheme"
import { KonanAPI } from "../../../../services/KonanAPI"
import type { PaginatedScriptGroupListList } from "../../../../types/generated/api/PaginatedScriptGroupListList"
import type { Script } from "../../../../types/generated/api/Script"
import type { ScriptSchema } from "../../../../types/generated/api/ScriptSchema"
import type { ScriptTemplateRetrieve } from "../../../../types/generated/api/ScriptTemplateRetrieve"
import { MarketPlaceIntegrationDialog } from "../../../MarketPlace/components/MarketPlaceIntegrationDialog"
import { ScriptDialog } from "../../../Scripts/ScriptDialog"
import { useScriptNodeForm } from "../../hooks/forms/useScriptNodeForm"
import { useNodeDeletion } from "../../hooks/useNodeHooks"
import type { NodeType, ScriptNodeProps } from "../../types/interfaces"
import { nodeHelpers } from "../../utils/nodeHelpers"
import { getTrimmedComputedFeatureName } from "../../utils/workflowHelpers"
import { SelectDataMenu } from "./SelectDataMenu"
import { BaseWorkflowNode } from "./base/BaseWorkflowNode"

/**
 * Script node component for workflow management
 * Handles script selection, configuration, and display in workflow graphs
 * Supports both marketplace and custom scripts with feature mapping
 *
 * @param props - Component props containing node data, view mode, and handlers
 * @returns React element representing the script node
 */
export function ScriptNode(props: Readonly<ScriptNodeProps>): React.ReactElement {
  const { data, menuView, id } = props
  const updateGraph = props.data?.updateGraph
  const scripts = data?.scripts
  const readMode: boolean | null = data?.readMode ?? null
  const nodeId = id ?? null

  // Hooks (navigation, theme, translation, React Flow)
  const { id: projectId } = useParams<{ id: string }>()
  const [searchParams] = useSearchParams()
  const theme = getTheme()
  const { t } = useTranslation("workflows")
  const store = useStoreApi()
  const { setNodes, getNodes } = store.getState()

  // Constants
  const page = 1
  const pageSize = 200

  // State hooks
  const [isScriptDialogOpen, setIsScriptDialogOpen] = useState<boolean>(false)
  const [isBuiltinScriptDialogOpen, setIsBuiltinScriptDialogOpen] = useState<boolean>(false)
  const [selectedTemplateValue, setSelectedTemplateValue] = useState<string | undefined | null>(
    data?.script ? (data?.script as Script)?.template : undefined,
  )
  const [selectedValue, setSelectedValue] = useState<Script | undefined | string>(
    data?.script ? (typeof data?.script === "string" ? data?.script : (data?.script as Script)?.uuid) : "",
  )

  // Data fetching queries
  const { data: scriptsData, isLoading: isScriptsLoading } = useQuery<
    AxiosResponse<PaginatedScriptGroupListList>,
    AxiosError
  >(
    ["scripts", projectId, page, pageSize, data?.scriptType],
    () => KonanAPI.fetchScripts(projectId as string, page, pageSize, undefined, data?.scriptType === "marketplace"),
    {
      enabled: !!projectId && !menuView && !readMode,
    },
  )

  // Custom hooks
  const formik = useScriptNodeForm({
    data: props?.data,
    nodeId,
    scripts: scriptsData?.data.results,
  })

  const { handleRemoveNode } = useNodeDeletion(nodeId as string, updateGraph)

  // Memoized values
  const currentScript = useMemo(() => {
    if (scriptsData && data?.script && readMode && !isScriptsLoading) {
      const script = scriptsData?.data?.results?.find((script) => script?.uuid === (data?.script as Script)?.uuid)
      if (script) {
        return script
      } else {
        return data?.script as Script
      }
    }
  }, [scriptsData, data?.script, readMode, isScriptsLoading])

  const { data: templateData, isLoading: isTemplateDataLoading } = useQuery<
    AxiosResponse<ScriptTemplateRetrieve>,
    AxiosError
  >(
    ["ready-template", projectId, selectedTemplateValue ?? !!(currentScript as Script)?.template],
    () =>
      KonanAPI.fetchReadyTemplate({
        projectId: projectId as string,
        templateId: selectedTemplateValue as string,
      }),
    {
      enabled: !!projectId && !!selectedTemplateValue,
    },
  )

  const nodeLogo = useMemo(() => {
    return data?.scriptType === "marketplace" ? (
      isTemplateDataLoading ? (
        <Skeleton variant="rectangular" width={"100%"} height={55} />
      ) : templateData?.data.logo && templateData?.data.logo ? (
        <img src={templateData?.data.logo} style={{ width: "100%", height: "55px", borderRadius: "4px" }} />
      ) : (
        <PolylineIcon sx={{ color: theme.palette.base.text[1] }} />
      )
    ) : (
      <CodeIcon sx={{ color: theme.palette.base.text[1] }} />
    )
  }, [data?.scriptType, isTemplateDataLoading, templateData?.data.logo, theme.palette.base.text])

  // Helper functions
  const currentData = (): PaginatedScriptGroupListList | undefined => {
    return scriptsData?.data ?? scripts
  }

  const getTransformedScriptData = (): PaginatedScriptGroupListList | undefined => {
    const scriptData = currentData()
    if (!scriptData?.results) return undefined

    return scriptData
  }

  const handleScriptSelection = (id: string): void => {
    const scriptData = currentData()
    const selectedScript = scriptData?.results?.find((script) => script.uuid === id)

    if (!selectedScript) return

    // Update selected values
    setSelectedValue(id)
    setSelectedTemplateValue(selectedScript.template)

    // Handle computed features
    let trimmedName: string | undefined = ""
    if (scriptData?.results) {
      trimmedName = getTrimmedComputedFeatureName(scriptData.results, id, "uuid")

      if (trimmedName) {
        handleUpdateComputedFeature(trimmedName)
      }
    }

    // Update node data with script-specific information
    setNodes(
      getNodes().map((node) => {
        const newNode = { ...node }
        if (node.id === nodeId && data?.nodeType) {
          newNode.data = {
            ...newNode.data,
            script: id,
            computed_feature_name: trimmedName,
            is_draft: selectedScript.template ? false : selectedScript.is_draft,
            isBuiltin: Boolean(selectedScript.template),
          }
        }
        return newNode
      }),
    )
  }

  const handleUpdateComputedFeature = (newFeature: string): void => {
    // check if this node has already an assigned computed feat name
    const computedFeatureIndex = props?.data?.formik?.values?.computedFeatures?.findIndex(
      (feature: { uuid: string; value: string }) => feature?.value?.toLowerCase() === newFeature?.toLowerCase(),
    )

    // check if it's a new computed feat name
    if (computedFeatureIndex === -1 && newFeature) {
      props?.data?.formik?.setFieldValue("computedFeatures", [
        ...(props?.data?.formik?.values?.computedFeatures ?? []),
        { uuid: nodeId, value: newFeature },
      ])
    } else if (computedFeatureIndex !== -1 && newFeature) {
      props?.data?.formik?.setFieldValue(`computedFeatures[${computedFeatureIndex}].value`, newFeature)
    }
  }

  const setFormikScriptData = (scriptSchema: ScriptSchema): void => {
    const formatResponseToKeyValue =
      scriptSchema?.input_features?.map((feature) => ({
        node_feature: feature?.name,
        workflow_feature:
          props?.data?.feature_mappings?.find((feat) => feat.node_feature === feature.name)?.workflow_feature ?? "",
        type: feature?.type ?? scriptSchema?.input_features.find((input) => input?.name === feature.name)?.type,
      })) ?? []

    formik?.setFieldValue("feature_mappings", formatResponseToKeyValue)
    formik?.setFieldValue("retrievedNode", {
      uuid: formik?.values?.retrievedNode?.uuid,
      feature_mappings: formatResponseToKeyValue,
    })

    // first we check if workflow not in read/view mode so it's either in create or edit
    if (!readMode && selectedValue) {
      // then we check if our cached scripts, has the current script uuid as a property or not
      if (formik?.values?.cachedNodes?.hasOwnProperty(selectedValue as string)) {
        // if already this script is cached then, update feature_mappings state with the cached/saved value
        formik?.setFieldValue("feature_mappings", formik?.values?.cachedNodes[selectedValue as string])
      }
      // if not, and workflow in create mode or the retrieved script uuid not equal to the current one
      // then we add its values as a new cached script item
      else if (
        (searchParams.get("workflowId") === "new" || formik?.values?.retrievedNode?.uuid !== selectedValue) &&
        !formik?.values?.cachedNodes?.hasOwnProperty(selectedValue as string)
      ) {
        const newCachedNodes = {
          ...formik?.values?.cachedNodes,
          [selectedValue as string]: formatResponseToKeyValue,
        }
        formik?.setFieldValue("cachedNodes", newCachedNodes)
        formik?.setFieldValue("feature_mappings", formatResponseToKeyValue)
      }
    }
  }

  // Node metadata (from centralized helpers)
  const metadata = nodeHelpers.getNodeMetadata("ScriptNode")

  // UseEffects
  /**
   * Effect: Updates node state when feature mappings change
   * Listens to changes in script/project node and updates the node state
   * Only runs when creating or updating workflow (not in read mode)
   */
  useEffect(() => {
    if (formik?.values?.feature_mappings && nodeId) {
      const { feature_mappings } = formik.values

      setTimeout(() => {
        setNodes(
          getNodes().map((node) => {
            const newNode = { ...node }
            if (node.id === nodeId) {
              newNode.data = {
                ...newNode.data,
                feature_mappings: feature_mappings ?? [],
              }
            }
            return newNode
          }),
        )
      }, 100)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.nodeType, formik.values?.feature_mappings, getNodes, nodeId])

  return (
    <Fragment>
      {/* Built-in Script Configuration Dialog */}
      {isBuiltinScriptDialogOpen && (
        <MarketPlaceIntegrationDialog
          isOpen
          onClose={() => {
            formik.setFieldValue("cachedNodes", {
              ...formik.values.cachedNodes,
              [selectedValue as string]: formik?.values?.feature_mappings,
            })
            setIsBuiltinScriptDialogOpen(false)
          }}
          scriptInfo={{
            name: data?.script?.name ?? (currentScript?.name as string),
            uuid: selectedTemplateValue as string,
            scriptId:
              typeof data?.script === "string" ? data?.script : (data?.script?.uuid ?? (currentScript?.uuid as string)),
          }}
          workflowSchema={props?.data?.formik?.values?.features}
          workflowFormik={formik}
          renderView="workflow"
          setFormikScriptData={setFormikScriptData}
        />
      )}

      {/* Custom Script Dialog */}
      {isScriptDialogOpen && (
        <ScriptDialog
          isOpen
          onClose={() => setIsScriptDialogOpen(false)}
          renderView="workflow"
          scriptId={selectedValue as string}
          isWorkFlowInReadMode={readMode}
        />
      )}

      {/* Main Node Card */}
      <BaseWorkflowNode
        id={nodeId as string}
        menuView={menuView}
        readMode={readMode ?? undefined}
        nodeType={data?.nodeType as string}
        title={data?.scriptType === "marketplace" ? t("scriptNode.labels.marketplace") : t("scriptNode.labels.script")}
        iconProps={{
          element: nodeLogo,
          className: metadata.iconClassName,
          width: data?.scriptType === "marketplace" && templateData?.data.logo ? "50px" : undefined,
        }}
        handlesProps={{ showTarget: !menuView, showSource: !menuView }}
        width={metadata.width}
        onRemove={() => handleRemoveNode()}
      >
        <Grid item xs={12} display="flex" justifyContent="space-between" alignItems="center">
          <Grid item xs={selectedValue ? 8 : 10}>
            {!menuView && !readMode ? (
              <SelectDataMenu
                nodeId={nodeId}
                dataType={data?.nodeType as NodeType}
                menuView={menuView}
                dataItemId={data?.script}
                itemData={getTransformedScriptData()}
                key={nodeId}
                selectedValue={selectedValue as string}
                setSelectedValue={(id) => handleScriptSelection(id as string)}
                isDataLoading={isScriptsLoading}
              />
            ) : (
              <Grid container justifyContent="space-between" alignItems="center">
                <ValueBlock
                  value={
                    (data?.script as Script)?.name ??
                    props?.data?.scriptName ??
                    t(
                      `selectionNode.nodeBodies.${data?.scriptType === "marketplace" ? "noIntegrationSelected" : "noScriptSelected"}`,
                    )
                  }
                  size={12}
                  showTooltip={!menuView}
                />
              </Grid>
            )}
          </Grid>

          {/* Action buttons */}
          <Grid container item xs={selectedValue ? 4 : 2} columnSpacing={0.5} ml={0}>
            {selectedValue && (
              <Grid item xs={6}>
                <Button
                  onClick={() => {
                    // Determine if selected script is built-in or custom
                    const selectedScript = (currentData() as PaginatedScriptGroupListList)?.results?.find(
                      (result) => result?.uuid === selectedValue,
                    )
                    if (selectedScript?.template || data?.script?.template) {
                      setIsBuiltinScriptDialogOpen(true)
                    } else {
                      setIsScriptDialogOpen(true)
                    }
                  }}
                  disabled={menuView || !selectedValue}
                  size={"regular"}
                  fullWidth
                  data-mode={readMode ? "read" : menuView ? "edit-view" : "edit"}
                  variant="secondary"
                >
                  <SettingsOutlinedIcon fontSize="small" sx={{ color: theme.palette.base.text[1] }} />
                </Button>
              </Grid>
            )}
            <Grid item xs={selectedValue ? 6 : 12}>
              <Button
                onClick={() => window.open(`/projects/${projectId}/Scripts`, "_blank")}
                disabled={menuView}
                size={"regular"}
                fullWidth
                data-mode={readMode ? "read" : menuView ? "edit-view" : "edit"}
                variant="secondary"
              >
                <LaunchOutlinedIcon fontSize="small" sx={{ color: theme.palette.base.text[1] }} />
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </BaseWorkflowNode>
    </Fragment>
  )
}
